/**
 * Activity Logger Utility
 * Handles logging user activities to the backend API
 * Includes offline queue support and authentication
 */

interface ActivityLog {
  eventType: string;
  metadata?: Record<string, unknown>;
  timestamp: string;
}

import { API_BASE_URL, apiLocaleHeaders } from './api';

const QUEUE_KEY = 'activity_log_queue';

// Store auth token getter function
let getAuthToken: (() => Promise<string | null>) | null = null;

/**
 * Initialize logger with auth token getter from Clerk
 * Call this from your app initialization
 */
export function initLogger(tokenGetter: () => Promise<string | null>) {
  getAuthToken = tokenGetter;
}

/**
 * Get queued logs from localStorage
 */
function getQueuedLogs(): ActivityLog[] {
  try {
    const queue = localStorage.getItem(QUEUE_KEY);
    return queue ? JSON.parse(queue) : [];
  } catch {
    return [];
  }
}

/**
 * Save logs to queue in localStorage
 */
function saveToQueue(logs: ActivityLog[]): void {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(logs));
  } catch (error) {
    console.error('Failed to save logs to queue:', error);
  }
}

/**
 * Clear the log queue
 */
export function clearActivityLogQueue(): void {
  localStorage.removeItem(QUEUE_KEY);
}

function clearQueue(): void {
  clearActivityLogQueue();
}

/**
 * Send logs to the backend API
 */
async function sendToApi(log: ActivityLog): Promise<boolean> {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...apiLocaleHeaders(),
    };

    // Add auth token if available
    if (getAuthToken) {
      const token = await getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    const response = await fetch(`${API_BASE_URL}/api/logs`, {
      method: 'POST',
      headers,
      body: JSON.stringify(log),
    });

    if (!response.ok) {
      console.error('Log API error:', response.status, await response.text());
      return false;
    }

    return true;
  } catch (error) {
    console.error('Failed to send log to API:', error);
    return false;
  }
}

/**
 * Process queued logs when online
 */
export async function processQueue(): Promise<void> {
  const queue = getQueuedLogs();
  if (queue.length === 0) return;

  const remainingLogs: ActivityLog[] = [];

  for (const log of queue) {
    const success = await sendToApi(log);
    if (!success) {
      remainingLogs.push(log);
    }
  }

  if (remainingLogs.length > 0) {
    saveToQueue(remainingLogs);
  } else {
    clearQueue();
  }
}

/**
 * Log user activity
 * @param eventType - Type of event (e.g., 'user_signed_in', 'page_view')
 * @param metadata - Additional event data
 */
export async function logActivity(
  eventType: string,
  metadata?: Record<string, unknown>
): Promise<void> {
  const log: ActivityLog = {
    eventType,
    metadata,
    timestamp: new Date().toISOString(),
  };

  // Check if online
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    // Queue log for later
    const queue = getQueuedLogs();
    queue.push(log);
    saveToQueue(queue);
    console.log('Offline: Log queued for later');
    return;
  }

  // Try to send log
  const success = await sendToApi(log);
  
  if (!success) {
    // Queue on failure
    const queue = getQueuedLogs();
    queue.push(log);
    saveToQueue(queue);
  }
}

// Process queued logs when coming back online
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    console.log('Back online: Processing queued logs');
    processQueue();
  });
}
